import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from '../../../i18n/useTranslation';
import type { PaymentRequestModel } from '../../../Models/PublicModel';
import { PublicService } from '../../../Services/PublicService';
import { formatDate } from '../../../Utils/DateUtil';
import { MoneyText } from '../../SharedArea/MoneyText/MoneyText';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { EmptyState } from '../../SharedArea/EmptyState/EmptyState';
import './PaymentRequest.css';

/**
 * The document a parent opens from a chat.
 *
 * No session, no sign-in, no app around it: the token in the link is the whole
 * capability. Everything shown was snapshotted when the tutor created it, so
 * the page cannot contradict what was sent even if the lessons change
 * afterwards.
 *
 * Mobile first, because this is opened on a phone almost every time. The
 * payment links come first, since paying is why the parent opened it.
 */
export function PaymentRequest() {
  const { token } = useParams();
  const { tr, language } = useTranslation();
  const [document, setDocument] = useState<PaymentRequestModel | null>(null);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) {
      setFailed(true);
      return;
    }
    PublicService.paymentRequest(token)
      .then(setDocument)
      .catch(() => setFailed(true));
  }, [token]);

  if (failed) {
    return <EmptyState title={tr('paymentRequestTitle')} hint={tr('paymentRequestInvalid')} />;
  }
  if (!document) return <Spinner />;

  const { business, payment } = document;
  const hasBank = Boolean(payment.bankName || payment.bankAccount);
  // Read in English, the page spells the bank and the holder in English where
  // the tutor gave one. A request frozen before those existed falls back.
  const bankName = language === 'en' ? payment.bankNameEn || payment.bankName : payment.bankName;
  const bankOwner = language === 'en' ? payment.bankOwnerEn || payment.bankOwner : payment.bankOwner;

  async function copyBank() {
    const lines = [
      bankName && `${tr('bankName')}: ${bankName}`,
      payment.bankBranch && `${tr('bankBranch')}: ${payment.bankBranch}`,
      payment.bankAccount && `${tr('bankAccount')}: ${payment.bankAccount}`,
      bankOwner && `${tr('bankOwner')}: ${bankOwner}`
    ].filter(Boolean).join('\n');
    try {
      await navigator.clipboard.writeText(lines);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is the browser's to refuse. The details are on
      // screen either way, which is why this fails quietly.
      setCopied(false);
    }
  }

  return (
    <article className="payment-request" dir={business.language === 'en' ? 'ltr' : 'rtl'}>
      <header className="payment-request-header">
        {business.logo && <img className="payment-request-logo" src={business.logo} alt="" />}
        <div>
          <p className="payment-request-business">{business.businessName ?? tr('appName')}</p>
          {business.phone && <p className="payment-request-meta"><bdi>{business.phone}</bdi></p>}
        </div>
      </header>

      <h1 className="payment-request-title">{tr('paymentRequestTitle')}</h1>
      <p className="payment-request-for">
        {document.recipientName ?? document.studentFirstName ?? document.student}
      </p>

      <p className="payment-request-total">
        <span>{tr('total')}</span>
        <MoneyText amount={document.total} />
      </p>

      {/* Paying is why the parent opened this, so the ways to pay come before
          the list of what is being paid for. */}
      {(payment.payboxLink || payment.bitLink || hasBank) && (
        <section className="payment-request-methods">
          <h2>{tr('howToPay')}</h2>
          {payment.payboxLink && (
            <a className="primary-button" href={payment.payboxLink} target="_blank" rel="noreferrer noopener">
              PayBox
            </a>
          )}
          {payment.bitLink && (
            <a className="primary-button" href={payment.bitLink} target="_blank" rel="noreferrer noopener">
              Bit
            </a>
          )}
          {hasBank && (
            <>
              <dl className="payment-request-bank">
                {bankName && (
                  <div><dt>{tr('bankName')}</dt><dd><bdi>{bankName}</bdi></dd></div>
                )}
                {payment.bankBranch && (
                  <div><dt>{tr('bankBranch')}</dt><dd><bdi>{payment.bankBranch}</bdi></dd></div>
                )}
                {payment.bankAccount && (
                  <div><dt>{tr('bankAccount')}</dt><dd><bdi>{payment.bankAccount}</bdi></dd></div>
                )}
                {bankOwner && (
                  <div><dt>{tr('bankOwner')}</dt><dd><bdi>{bankOwner}</bdi></dd></div>
                )}
              </dl>
              {/* One button for the whole block. Copying a bank transfer means
                  moving four separate numbers into a banking app, and doing
                  that four times on a phone is where the digits get dropped. */}
              <button type="button" className="ghost-button payment-request-copy" onClick={() => void copyBank()}>
                {copied ? tr('copied') : tr('copyBankDetails')}
              </button>
            </>
          )}
        </section>
      )}

      <section className="payment-request-lessons">
        <h2>{tr('lessonsInThisRequest')}</h2>
        <ul>
          {document.lessons.map(lesson => (
            <li key={lesson.id}>
              <span>
                <bdi>{formatDate(lesson.date, language)}</bdi>
                {lesson.time && <> · <bdi>{lesson.time}</bdi></>}
                {/* A charged cancellation on an invoice looks like a mistake
                    unless it says what it is. Saying so here is what stops
                    the question coming back as a message. */}
                {lesson.status === 'cancelled_charged' && (
                  <em className="payment-request-cancelled">{tr('stCancelledCharged')}</em>
                )}
              </span>
              <MoneyText amount={lesson.amount} />
            </li>
          ))}
        </ul>
      </section>

      {business.signatureName && (
        <p className="payment-request-signature">
          <span>{tr('sincerely')}</span>
          <strong>{business.signatureName}</strong>
        </p>
      )}

      {business.footerText && <p className="payment-request-footer">{business.footerText}</p>}

      <p className="payment-request-issued">
        {tr('issuedOn')} <bdi>{formatDate(document.issuedAt.slice(0, 10), language)}</bdi>
      </p>

      <button type="button" className="ghost-button payment-request-print" onClick={() => window.print()}>
        {tr('print')}
      </button>
    </article>
  );
}
